import React, { useState, useMemo } from 'react';
import { X, TrendingUp, Users, Target, Zap, BarChart3, ChevronRight } from 'lucide-react';

// UK postcode prefix → approximate [lat, lon]
const PREFIX_GEO = {
  SW:[51.47,-0.17],SE:[51.48,-0.05],E:[51.55,0.05],EC:[51.52,-0.09],
  N:[51.57,-0.1],NW:[51.55,-0.18],W:[51.51,-0.22],WC:[51.52,-0.12],
  KT:[51.38,-0.3],GU:[51.24,-0.77],RH:[51.17,-0.17],SM:[51.37,-0.17],
  TW:[51.45,-0.35],CR:[51.37,-0.1],TN:[51.13,0.27],
  M:[53.48,-2.24],L:[53.41,-2.98],B:[52.48,-1.9],LS:[53.8,-1.55],
  S:[53.38,-1.47],NG:[52.95,-1.15],EH:[55.95,-3.19],G:[55.86,-4.25],
  CF:[51.48,-3.18],BS:[51.45,-2.59],NE:[54.98,-1.61],BN:[50.82,-0.14],
  OX:[51.75,-1.26],CB:[52.21,0.12],SO:[50.9,-1.4],PO:[50.8,-1.09],
  EX:[50.72,-3.53],PL:[50.37,-4.14],LE:[52.63,-1.13],DE:[52.92,-1.47],
  CV:[52.41,-1.51],WV:[52.59,-2.13],ST:[52.98,-2.18],GL:[51.87,-2.24],
  SN:[51.56,-1.78],BA:[51.38,-2.36],BD:[53.79,-1.76],YO:[53.96,-1.08],
  HU:[53.74,-0.34],DN:[53.52,-1.13],LN:[53.23,-0.54],PE:[52.57,-0.24],
  IP:[52.06,1.16],NR:[52.63,1.3],CO:[51.89,0.9],CM:[51.73,0.47],
  SS:[51.54,0.71],RM:[51.57,0.18],DA:[51.44,0.21],ME:[51.39,0.54],
  CT:[51.28,1.08],EN:[51.65,-0.08],HA:[51.58,-0.34],UB:[51.53,-0.44],
  SL:[51.51,-0.59],HP:[51.75,-0.74],AL:[51.75,-0.34],MK:[52.04,-0.76],
  NN:[52.24,-0.9],RG:[51.45,-0.97],WA:[53.39,-2.59],CH:[53.19,-2.89],
  CW:[53.09,-2.44],SK:[53.39,-2.16],OL:[53.54,-2.12],BL:[53.58,-2.43],
  WN:[53.55,-2.63],PR:[53.76,-2.7],BB:[53.76,-2.48],FY:[53.84,-3.05],
  LA:[54.05,-2.8],CA:[54.66,-2.95],DL:[54.52,-1.56],TS:[54.58,-1.21],
  SR:[54.91,-1.38],DH:[54.78,-1.57],AB:[57.15,-2.09],DD:[56.46,-2.97],
  PH:[56.7,-3.78],FK:[56.12,-3.93],KY:[56.21,-3.18],KA:[55.46,-4.63],
  DG:[55.07,-3.61],ML:[55.77,-3.69],PA:[55.84,-4.43],IV:[57.48,-4.22],
  BT:[54.6,-5.93],LL:[53.13,-3.83],SY:[52.71,-2.75],SA:[51.62,-3.94],
  NP:[51.59,-2.99],LD:[52.25,-3.38],HR:[52.06,-2.72],WR:[52.19,-2.22],
  DT:[50.71,-2.44],BH:[50.72,-1.88],SP:[51.07,-1.79],TA:[51.02,-3.1],
};

// Convert lat/lon to SVG coordinates (viewBox 0 0 500 780)
function geoToSvg(lat, lon) {
  const x = (lon + 8.0) / 10.2 * 500;
  const y = (59.0 - lat) / 9.5 * 780;
  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
}

function getAreaCenter(prefixes) {
  if (!prefixes || prefixes.length === 0) return null;
  let totalLat = 0, totalLon = 0, count = 0;
  for (const p of prefixes) {
    const norm = p.toUpperCase().replace(/[^A-Z]/g, '');
    if (PREFIX_GEO[norm]) {
      totalLat += PREFIX_GEO[norm][0];
      totalLon += PREFIX_GEO[norm][1];
      count++;
    }
  }
  if (count === 0) return null;
  return geoToSvg(totalLat / count, totalLon / count);
}

const VIEW_MODES = [
  { id: 'density', label: 'Waitlist Density', icon: Users },
  { id: 'balance', label: 'Supply / Demand', icon: BarChart3 },
];

function getDensityColor(count, maxCount, areaStatus) {
  if (areaStatus === 'live') return { fill: '#10b981', stroke: '#059669', label: 'Live area' };
  if (maxCount === 0) return { fill: '#94a3b8', stroke: '#64748b', label: 'No data' };
  const pct = count / maxCount;
  if (pct >= 0.7) return { fill: '#ef4444', stroke: '#dc2626', label: 'High demand' };
  if (pct >= 0.4) return { fill: '#f59e0b', stroke: '#d97706', label: 'Moderate demand' };
  if (pct >= 0.1) return { fill: '#3b82f6', stroke: '#2563eb', label: 'Growing interest' };
  return { fill: '#94a3b8', stroke: '#64748b', label: 'Low signal' };
}

function getBalanceColor(trades, employers) {
  const total = trades + employers;
  if (total === 0) return { fill: '#94a3b8', stroke: '#64748b', label: 'No data' };
  const ratio = total > 0 ? trades / total : 0;
  if (ratio >= 0.4 && ratio <= 0.6) return { fill: '#10b981', stroke: '#059669', label: 'Balanced' };
  if (ratio > 0.6) return { fill: '#8b5cf6', stroke: '#7c3aed', label: 'Needs employers' };
  return { fill: '#f97316', stroke: '#ea580c', label: 'Needs trades' };
}

function getBubbleSize(count, maxCount, areaStatus) {
  // Live areas get a base boost since they have registered users, not waitlist entries
  const statusBoost = areaStatus === 'live' ? 8 : areaStatus === 'priority' ? 4 : 0;
  const minR = 18, maxR = 48;
  if (maxCount === 0) return minR + statusBoost;
  return Math.min(maxR, minR + statusBoost + (count / Math.max(maxCount, 1)) * (maxR - minR));
}

// Great Britain mainland outline (simplified coastline, viewBox 0 0 500 780)
const GB_PATH = `M112.3,733.2 L120.6,729.1 L144.6,716.8 L157.8,711.9 L188.7,722.5 L213.7,720.9 L271.6,693.8 L295.6,688.0 L316.7,679.8 L335.3,675.7 L339.7,679.0 L360.3,674.9 L385.3,670.8 L405.4,674.9 L427.5,663.4 L446.1,651.1 L459.3,644.5 L461.8,626.5 L443.6,619.9 L435.3,615.0 L442.2,596.9 L450.0,592.8 L471.6,569.8 L478.4,536.1 L477.0,521.4 L416.7,498.4 L399.5,492.6 L394.6,465.5 L396.1,445.8 L388.7,432.7 L383.8,404.0 L373.0,387.5 L362.3,370.3 L321.6,330.9 L317.2,314.5 L311.3,298.0 L299.5,273.4 L294.1,265.2 L268.6,246.3 L258.8,239.7 L252.5,229.9 L255.9,219.2 L257.4,209.4 L265.7,202.8 L275.5,186.4 L290.2,164.2 L291.7,150.3 L295.1,137.1 L295.1,123.2 L291.7,108.4 L230.4,106.7 L201.0,114.9 L181.4,82.1 L166.7,61.6 L156.9,49.3 L147.1,34.5 L149.5,32.8 L142.2,45.2 L147.1,57.5 L142.2,82.1 L137.3,98.5 L122.5,123.2 L115.2,139.6 L111.8,156.0 L105.4,176.5 L117.6,188.8 L112.7,205.3 L122.5,213.5 L117.6,223.3 L127.5,238.1 L154.4,250.4 L152.0,257.0 L155.4,262.7 L156.9,279.2 L161.8,291.5 L161.8,303.8 L144.6,324.3 L142.2,340.7 L149.5,348.9 L215.7,338.3 L230.4,340.7 L242.6,336.6 L216.7,369.5 L224.5,394.1 L242.6,420.4 L242.6,430.2 L240.2,458.1 L220.6,466.4 L204.9,469.6 L188.7,473.7 L175.5,468.0 L169.1,472.1 L161.8,502.5 L164.2,510.7 L192.2,540.3 L168.1,569.0 L148.5,585.4 L132.4,599.4 L142.2,605.9 L161.8,607.6 L186.3,611.7 L206.9,609.2 L236.3,619.9 L238.2,638.8 L215.7,642.1 L186.3,656.8 L160.8,683.1 L144.6,699.5 L125.0,722.5 L112.3,733.2 Z`;

// Northern Ireland outline
const NI_PATH = `M104.4,404.0 L119.6,398.2 L120.1,390.0 L117.6,381.0 L111.8,373.6 L101.5,365.4 L103.4,361.3 L106.4,351.4 L108.8,344.8 L104.4,328.4 L98.0,320.2 L88.2,312.0 L73.5,303.8 L53.9,299.7 L34.3,297.2 L30.4,306.3 L32.8,320.2 L34.3,336.6 L29.4,348.9 L22.1,361.3 L19.6,373.6 L22.1,385.9 L31.9,395.7 L44.1,406.4 L59.8,410.5 L78.4,408.9 L93.1,410.5 L100.5,408.9 L104.4,404.0 Z`;

export function ExpansionHeatmap({ areas }) {
  const [viewMode, setViewMode] = useState('density');
  const [selectedArea, setSelectedArea] = useState(null);
  const [hoveredArea, setHoveredArea] = useState(null);

  const bubbles = useMemo(() => {
    if (!areas || areas.length === 0) return [];
    const maxCount = Math.max(...areas.map(a => a.total_waitlist_count || 0), 1);

    const result = areas.map(area => {
      const center = getAreaCenter(area.postcode_prefixes);
      if (!center) return null;

      const count = area.total_waitlist_count || 0;
      const trades = area.tradesperson_count || 0;
      const employers = area.employer_count || 0;

      const color = viewMode === 'density'
        ? getDensityColor(count, maxCount, area.area_status)
        : getBalanceColor(trades, employers);

      const radius = getBubbleSize(count, maxCount, area.area_status);

      return { ...area, cx: center.x, cy: center.y, radius, color, count, trades, employers };
    }).filter(Boolean);

    // Nudge overlapping bubbles apart
    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const a = result[i], b = result[j];
        const dx = b.cx - a.cx, dy = b.cy - a.cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = a.radius + b.radius + 4;
        if (dist < minDist && dist > 0) {
          const push = (minDist - dist) / 2;
          const nx = dx / dist, ny = dy / dist;
          a.cx -= nx * push; a.cy -= ny * push;
          b.cx += nx * push; b.cy += ny * push;
        }
      }
    }
    return result;
  }, [areas, viewMode]);

  const maxCount = Math.max(...(areas || []).map(a => a.total_waitlist_count || 0), 1);

  const legendItems = viewMode === 'density'
    ? [
        { color: '#10b981', label: 'Live area' },
        { color: '#ef4444', label: 'High demand' },
        { color: '#f59e0b', label: 'Moderate' },
        { color: '#3b82f6', label: 'Growing' },
        { color: '#94a3b8', label: 'Low signal' },
      ]
    : [
        { color: '#10b981', label: 'Balanced (launch-ready)' },
        { color: '#8b5cf6', label: 'Needs employers' },
        { color: '#f97316', label: 'Needs trades' },
        { color: '#94a3b8', label: 'No data' },
      ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="expansion-heatmap">
      {/* Header with toggle */}
      <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold text-slate-900">Expansion Heatmap</h3>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {VIEW_MODES.map(mode => (
            <button key={mode.id} onClick={() => setViewMode(mode.id)} data-testid={`heatmap-toggle-${mode.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === mode.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <mode.icon className="h-3.5 w-3.5" />
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Map Area */}
        <div className="flex-1 relative p-4 min-h-[420px]">
          <svg viewBox="0 0 500 780" className="w-full h-full max-h-[520px]" style={{ maxWidth: 380, margin: '0 auto', display: 'block' }}>
            {/* Great Britain outline */}
            <path d={GB_PATH} fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1.5" />
            {/* Northern Ireland outline */}
            <path d={NI_PATH} fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1.5" />
            
            {/* Bubbles */}
            {bubbles.map(b => (
              <g key={b.region_id}
                onClick={() => setSelectedArea(b.region_id === selectedArea ? null : b.region_id)}
                onMouseEnter={() => setHoveredArea(b.region_id)}
                onMouseLeave={() => setHoveredArea(null)}
                className="cursor-pointer"
                data-testid={`heatmap-bubble-${b.region_id}`}>
                {/* Glow for selected */}
                {selectedArea === b.region_id && (
                  <circle cx={b.cx} cy={b.cy} r={b.radius + 6} fill="none" stroke={b.color.stroke} strokeWidth="2" strokeDasharray="4 2" opacity="0.5">
                    <animate attributeName="r" values={`${b.radius + 4};${b.radius + 8};${b.radius + 4}`} dur="2s" repeatCount="indefinite" />
                  </circle>
                )}
                {/* Main bubble */}
                <circle cx={b.cx} cy={b.cy} r={b.radius} fill={b.color.fill} stroke={b.color.stroke} strokeWidth="1.5"
                  opacity={hoveredArea === b.region_id || selectedArea === b.region_id ? 0.95 : 0.7}
                  style={{ transition: 'opacity 0.2s, r 0.2s' }} />
                {/* Label */}
                <text x={b.cx} y={b.cy - 2} textAnchor="middle" fill="white" fontSize="10" fontWeight="700" pointerEvents="none">
                  {b.count}
                </text>
                <text x={b.cx} y={b.cy + 10} textAnchor="middle" fill="white" fontSize="7" fontWeight="500" pointerEvents="none" opacity="0.9">
                  {b.area_name?.length > 14 ? b.area_name.slice(0, 12) + '...' : b.area_name}
                </text>
              </g>
            ))}

            {/* Hover tooltip */}
            {hoveredArea && !selectedArea && (() => {
              const b = bubbles.find(x => x.region_id === hoveredArea);
              if (!b) return null;
              const tx = Math.min(Math.max(b.cx, 80), 420);
              const ty = b.cy - b.radius - 55;
              return (
                <g pointerEvents="none">
                  <rect x={tx - 75} y={ty} width="150" height="48" rx="6" fill="#1e293b" opacity="0.95" />
                  <text x={tx} y={ty + 16} textAnchor="middle" fill="white" fontSize="10" fontWeight="700">{b.area_name}</text>
                  <text x={tx} y={ty + 30} textAnchor="middle" fill="#94a3b8" fontSize="8">
                    {b.trades}T / {b.employers}E — {b.color.label}
                  </text>
                  <text x={tx} y={ty + 42} textAnchor="middle" fill="#94a3b8" fontSize="8">
                    Score: {b.launch_readiness_score} | Ratio: {b.trades_to_employers_ratio}
                  </text>
                </g>
              );
            })()}
          </svg>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-3 mt-3 px-2">
            {legendItems.map(item => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-slate-500">{item.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
              <span className="w-3 h-3 rounded-full bg-slate-300" />
              <span className="text-xs text-slate-400">small</span>
              <span className="w-5 h-5 rounded-full bg-slate-300" />
              <span className="text-xs text-slate-400">large = more signups</span>
            </div>
          </div>
        </div>

        {/* Detail Panel */}
        <div className="lg:w-72 border-t lg:border-t-0 lg:border-l border-slate-100 bg-slate-50/50">
          {selectedArea ? (() => {
            const area = bubbles.find(x => x.region_id === selectedArea);
            if (!area) return null;
            const LABEL_COLORS = {
              recommended_next_launch: 'bg-emerald-100 text-emerald-700',
              close_to_launch: 'bg-blue-100 text-blue-700',
              needs_more_employers: 'bg-orange-100 text-orange-700',
              needs_more_trades: 'bg-purple-100 text-purple-700',
              too_early: 'bg-slate-100 text-slate-600',
            };
            const STATUS_COLORS = {
              live: 'bg-emerald-500', priority: 'bg-amber-500', early_interest: 'bg-slate-400',
            };
            return (
              <div className="p-4 space-y-3" data-testid="heatmap-detail-panel">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-900 text-sm">{area.area_name}</h4>
                  <button onClick={() => setSelectedArea(null)} className="text-slate-400 hover:text-slate-600" data-testid="close-detail-panel">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[area.area_status] || 'bg-slate-400'}`} />
                  <span className="text-xs font-medium text-slate-600 capitalize">{area.area_status?.replace('_', ' ')}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${LABEL_COLORS[area.recommendation_label] || LABEL_COLORS.too_early}`}>
                    {area.recommendation_label?.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                    <p className="text-lg font-bold text-slate-900">{area.count}</p>
                    <p className="text-xs text-slate-500">Total Waitlist</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                    <p className="text-lg font-bold text-orange-600">{area.launch_readiness_score}</p>
                    <p className="text-xs text-slate-500">Readiness</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                    <p className="text-lg font-bold text-purple-600">{area.trades}</p>
                    <p className="text-xs text-slate-500">Trades</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                    <p className="text-lg font-bold text-blue-600">{area.employers}</p>
                    <p className="text-xs text-slate-500">Employers</p>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>S/D Ratio</span>
                    <span className="font-semibold text-slate-900">{area.trades_to_employers_ratio}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Urgent Trades</span>
                    <span className="font-semibold text-slate-900">{area.urgent_tradesperson_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Urgent Employers</span>
                    <span className="font-semibold text-slate-900">{area.urgent_employer_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>7-day growth</span>
                    <span className="font-semibold text-emerald-600">+{area.growth_last_7_days || 0}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>30-day growth</span>
                    <span className="font-semibold text-slate-900">+{area.growth_last_30_days || 0}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Trade variety</span>
                    <span className="font-semibold text-slate-900">{area.trade_variety || 0} types</span>
                  </div>
                </div>

                {area.top_trades?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1.5">Top Trades</p>
                    <div className="flex flex-wrap gap-1">
                      {area.top_trades.map(t => (
                        <span key={`detail-${area.region_id}-${t.trade}`} className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 text-xs rounded">
                          {t.trade} ({t.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(area.postcode_prefixes || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1.5">Postcodes</p>
                    <div className="flex flex-wrap gap-1">
                      {area.postcode_prefixes.map(p => (
                        <span key={`detail-${area.region_id}-${p}`} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded font-mono">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="p-6 flex flex-col items-center justify-center h-full min-h-[200px] text-center">
              <ChevronRight className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-sm text-slate-400 font-medium">Click a bubble to see area details</p>
              <p className="text-xs text-slate-300 mt-1">Hover for a quick preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
